import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Check, Loader2 } from "lucide-react";
import { CoreDrawer } from "@/components/common/CoreDrawer";
import { EnhancedVoiceSelectorV2 } from "@/components/voice/EnhancedVoiceSelectorV2";
import { useUpdateAgent } from "@/hooks/agent/useAgents";
import { usePreselectedVoices } from "@/hooks/voice/usePreselectedVoices";
import { useVoices } from "@/hooks/voice/useVoices";
import type { Agent } from "@/types/agent/agent";

interface VoiceModalProps {
  genie: Agent | null;
  isOpen: boolean;
  onClose: () => void;
}

/**
 * VoiceModal — one-click "give it a voice" step for the setup checklist.
 * Uses the same curated selector as the advanced voice settings.
 */
export const VoiceModal = ({ genie, isOpen, onClose }: VoiceModalProps) => {
  const updateAgent = useUpdateAgent();
  const { voices, isLoading: voicesLoading } = usePreselectedVoices();
  const { data: voicesResult } = useVoices();

  const cc = genie?.settings?.conversation_config;
  const [voiceId, setVoiceId] = useState(cc?.tts?.voice_id ?? "");

  const initialVoiceId = useMemo(() => cc?.tts?.voice_id ?? "", [cc?.tts?.voice_id]);
  const dirty = voiceId !== initialVoiceId;

  useEffect(() => {
    if (isOpen) setVoiceId(initialVoiceId);
  }, [initialVoiceId, isOpen]);

  const handleClose = () => {
    onClose();
  };

  const handleSave = async () => {
    if (!genie) return;
    try {
      await updateAgent.mutateAsync({
        agent: genie,
        updateData: {
          settings: {
            ...genie.settings,
            conversation_config: {
              ...cc,
              tts: {
                ...cc?.tts,
                voice_id: voiceId,
              },
            },
          },
        },
      });
      toast.success("Voice updated");
      handleClose();
    } catch {
      toast.error("We couldn't save that voice. Please try again.");
    }
  };

  if (!genie) return null;

  return (
    <CoreDrawer
      isOpen={isOpen}
      onClose={handleClose}
      title="Give it a voice"
      description={`Pick how ${genie.name} sounds on calls and preview it before you save.`}
      className="sm:!max-w-[50vw]"
    >
      <div className="flex flex-col gap-2 py-2">
        {voicesLoading ? (
          <div className="flex items-center gap-2 px-2 py-8 text-sm text-gray-500">
            <Loader2 className="w-4 h-4 animate-spin" />
            Loading voices…
          </div>
        ) : (
          <EnhancedVoiceSelectorV2
            value={voiceId}
            onChange={setVoiceId}
            voices={voices}
            voicesLoading={false}
            currentVoice={voicesResult?.voices.find((voice) => voice.id === voiceId)}
            restrictToVoices
            hidePlayButton
          />
        )}

        <div className="flex gap-3 pt-4 border-t mt-2">
          <button
            type="button"
            onClick={handleSave}
            disabled={updateAgent.isPending || !dirty}
            className="flex items-center gap-2 px-6 py-2 text-sm font-semibold text-white rounded-md bg-gradient-to-r from-genie-primary to-genie-secondary hover:from-genie-primary-hover hover:to-genie-secondary transition-opacity disabled:opacity-50"
          >
            {updateAgent.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            {updateAgent.isPending ? "Saving…" : "Save voice"}
          </button>
          <button
            type="button"
            onClick={handleClose}
            disabled={updateAgent.isPending}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
        </div>
      </div>
    </CoreDrawer>
  );
};

export default VoiceModal;
